// Thin LOCAL fetch client for the patient conversation endpoints (multi-thread messaging).
// Deliberately NOT in the published @forcebase-ai SDK yet (local-first — folding in + publishing is
// still pending). Mirrors the SDK HttpClient shape: baseUrl + getToken → Bearer, credentials:"include"
// (dev cookie), unwraps the { data } envelope, and surfaces the API's { error:{code,message} } so
// callers can react to e.g. 422 invalid_subject / 403 not_in_care_team. zod isn't a web dependency,
// so shapes are validated with light runtime guards instead.
import { useMemo } from "react";
import { API_ORIGIN } from "../config";
import { useAppAuth } from "../auth";

export interface CareTeamMember {
  id: string;
  first_name: string;
  last_name: string;
  specialty: string | null;
  avatar_url: string | null;
}
export interface ConversationDoctor {
  id: string;
  first_name: string;
  last_name: string;
  specialty: string | null;
  avatar_url: string | null;
}
export interface ConversationLastMessage {
  preview: string | null;
  sender_role: string;
  created_at: string;
}
export interface ConversationSummary {
  thread_id: string;
  subject: string | null;
  doctor: ConversationDoctor | null;
  last_message: ConversationLastMessage | null;
  unread: number;
}
export interface ConversationMessage {
  id: string;
  sender_role: string;
  body: string;
  created_at: string;
}
export interface ConversationThread {
  thread_id: string;
  messages: ConversationMessage[];
}

/** Carries the API's PHI-safe error code + message (e.g. invalid_subject, not_in_care_team). */
export class ConvApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
    this.name = "ConvApiError";
  }
}

export interface ConversationsApi {
  careTeam(): Promise<CareTeamMember[]>;
  conversations(): Promise<ConversationSummary[]>;
  createConversation(input: { doctor_id: string; subject?: string }): Promise<{ thread_id: string }>;
  messages(threadId: string): Promise<ConversationThread>;
  sendMessage(threadId: string, body: string): Promise<{ id: string; created_at: string }>;
  unreadCount(): Promise<number>;
}

export function useConversationsApi(): ConversationsApi {
  const { getToken } = useAppAuth();
  return useMemo<ConversationsApi>(() => {
    async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
      const headers: Record<string, string> = {};
      if (body !== undefined) headers["Content-Type"] = "application/json";
      const token = await getToken();
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const res = await fetch(`${API_ORIGIN}${path}`, {
        method,
        headers,
        credentials: "include",
        body: body === undefined ? undefined : JSON.stringify(body),
      });
      const text = await res.text();
      const json: unknown = text ? JSON.parse(text) : {};
      if (!res.ok) {
        const err = (json as { error?: { code?: string; message?: string } }).error;
        throw new ConvApiError(res.status, err?.code ?? "error", err?.message ?? "Request failed");
      }
      return (json as { data?: T }).data as T;
    }
    return {
      careTeam: () => request<CareTeamMember[]>("GET", "/v1/patient/care-team").then((d) => d ?? []),
      conversations: () =>
        request<ConversationSummary[]>("GET", "/v1/patient/conversations").then((d) => d ?? []),
      createConversation: (input) =>
        request<{ thread_id: string }>("POST", "/v1/patient/conversations", input),
      messages: (threadId) =>
        request<ConversationThread>(
          "GET",
          `/v1/patient/conversations/${encodeURIComponent(threadId)}/messages`,
        ).then((d) => ({ thread_id: d?.thread_id ?? threadId, messages: d?.messages ?? [] })),
      sendMessage: (threadId, body) =>
        request<{ id: string; created_at: string }>(
          "POST",
          `/v1/patient/conversations/${encodeURIComponent(threadId)}/messages`,
          { body },
        ),
      unreadCount: () =>
        request<{ unread: number }>("GET", "/v1/patient/messages/unread-count")
          .then((d) => d?.unread ?? 0)
          .catch(() => 0),
    };
  }, [getToken]);
}
